import { API_ENDPOINTS } from '../config/api';
import { makeAuthenticatedRequest, createApiError } from './apiService';

export type ExpiryStatus = 'None' | 'Unknown' | 'OK' | 'ExpiringSoon' | 'Overdue';

export interface PortalCredential {
  stateCode: string;
  stateName: string;
  /** Null for an API account: its password lives in AASConfig, not in a portal secret. */
  credentialKey: string | null;
  /** The state signs in through an API with a username and password kept in AASConfig. */
  isApiAccount: boolean;
  username: string | null;
  hasCredential: boolean;
  passwordExpiryEnabled: boolean;
  passwordRotationDays: number | null;
  passwordLastChangedAt: string | null;
  passwordExpiryReminderDays: number | null;
  expiryStatus: ExpiryStatus;
  expiryDate: string | null;
  inactivityRevokeEnabled: boolean;
  inactivityRevokeDays: number | null;
  inactivityReminderDays: number | null;
  inactivityStatus: ExpiryStatus;
  lastActivityAt: string | null;
  inactivityRevokeDate: string | null;
}

export interface UpdateExpiryConfigRequest {
  passwordExpiryEnabled: boolean;
  passwordRotationDays?: number | null;
  passwordExpiryReminderDays?: number | null;
  passwordLastChangedAt?: string | null;
  inactivityRevokeEnabled?: boolean | null;
  inactivityRevokeDays?: number | null;
  inactivityReminderDays?: number | null;
}

export const listPortalCredentials = async (): Promise<PortalCredential[]> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.ADMIN_PORTAL_CREDENTIALS.LIST);
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

export const updatePortalCredential = async (
  stateCode: string,
  username: string,
  password: string
): Promise<void> => {
  const response = await makeAuthenticatedRequest(
    API_ENDPOINTS.ADMIN_PORTAL_CREDENTIALS.UPDATE(stateCode),
    {
      method: 'PUT',
      body: JSON.stringify({ username, password }),
    }
  );
  if (!response.ok) throw await createApiError(response);
};

export const updatePortalCredentialExpiry = async (
  stateCode: string,
  req: UpdateExpiryConfigRequest
): Promise<void> => {
  const response = await makeAuthenticatedRequest(
    API_ENDPOINTS.ADMIN_PORTAL_CREDENTIALS.UPDATE_EXPIRY(stateCode),
    {
      method: 'PATCH',
      body: JSON.stringify(req),
    }
  );
  if (!response.ok) throw await createApiError(response);
};

/** North Carolina STARS automatic password rotation (NcStarsRotationService). Never carries a password. */
export interface NcRotationStatus {
  environment: string;
  /** Rotation only ever runs in production. */
  runsHere: boolean;
  enabled: boolean;
  hold: string | null;
  lastRunDate: string | null;
  mirrorSecrets: string;
  passwordLastChangedAt: string | null;
  nextRotationAt: string | null;
  credentialReadable: boolean;
  /** An unfinished rotation left a pending password; nothing signs on to NC until it is settled. */
  pending: boolean;
  pendingSince: string | null;
}

export interface NcResolveResult {
  choice: string;
  results: { secret: string; ok: boolean; error: string | null }[];
  next: string;
}

const ncRotationRequest = async (url: string, init?: RequestInit) => {
  const response = await makeAuthenticatedRequest(url, init);
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

export const getNcRotationStatus = (): Promise<NcRotationStatus> =>
  ncRotationRequest(API_ENDPOINTS.ADMIN_NC_ROTATION.STATUS);

export const setNcRotationEnabled = (enabled: boolean): Promise<{ enabled: boolean }> =>
  ncRotationRequest(API_ENDPOINTS.ADMIN_NC_ROTATION.ENABLED, { method: 'PUT', body: JSON.stringify({ enabled }) });

/** Signs on to STARS and changes the password now. About 15 seconds. */
export const rotateNcNow = (): Promise<{ outcome: string }> =>
  ncRotationRequest(API_ENDPOINTS.ADMIN_NC_ROTATION.ROTATE_NOW, { method: 'POST' });

export const clearNcRotationHold = (): Promise<{ hold: null }> =>
  ncRotationRequest(API_ENDPOINTS.ADMIN_NC_ROTATION.CLEAR_HOLD, { method: 'POST' });

export const resolveNcRotation = (usePending: boolean): Promise<NcResolveResult> =>
  ncRotationRequest(API_ENDPOINTS.ADMIN_NC_ROTATION.RESOLVE, { method: 'POST', body: JSON.stringify({ usePending }) });

export interface PortalHealthResult {
  stateCode: string;
  stateName: string;
  ok: boolean;
  error: string | null;
  durationMs: number;
  checkedAt: string;
  scope: string;
}

/**
 * Logs into one state's portal and reports whether it worked.
 *
 * Slow on purpose — it drives a real browser, so 30 to 180 seconds is normal and Utah waits up to
 * 180s for its MFA email. No timeout is set here: the ALB allows 900s, and giving up early would
 * report a working state as broken.
 */
export const checkPortalHealth = async (stateCode: string): Promise<PortalHealthResult> => {
  const response = await makeAuthenticatedRequest(
    API_ENDPOINTS.ADMIN_PORTAL_HEALTH.CHECK(stateCode),
    { method: 'POST' }
  );
  if (!response.ok) throw await createApiError(response);
  return response.json();
};
